import { Button, CpfInput, IconWhats, Spinner, Text } from 'condolivre-ds';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { useCheckDocumentLazyQuery } from '../../../generated';
import { Layout } from '../../components/Layout/Layout';
import { encryptString } from '../../helpers/index';
import { gtmShootEvent, tagScreenEnum } from '../../helpers/tag';
import withApollo from '../../hoc/withApollo';
import withoutAuth from '../../hoc/withoutAuth';
import styles from './index.module.scss';
import { formModel, validateModel } from './model';
import { usePartner } from '../../providers/Partner';
import styled from 'styled-components';
import { css } from '@emotion/css';

const StyledDotedText = styled(Text)`
	color: white;
	&::after {
		background-color: ${props => props.color};
	}
`;

export default withoutAuth(
	withApollo(function Login() {
		const [form, setForm] = useState(formModel);
		const [validateRules, setValidateRules] = useState(validateModel);
		const [validateReady, setValidateReady] = useState(false);
		const [validateForgotReady, setValidateForgotReady] = useState(false);
		const router = useRouter();

		const status = {
			success: 'success',
			error: 'error',
		};

		const responseStatus = {
			not_found: 'NOT_FOUND',
			first_access: 'FIRST_ACCESS',
			active: 'ACTIVE',
			prospect: 'PROSPECT',
			onboard: 'ONBOARD',
		};

		useEffect(() => {
			if (validateReady) {
				validate();
			}
			if (validateForgotReady) {
				validateForgot();
			}
		}, [form]);

		const validate = () => {
			setValidateReady(true);
			setValidateRules({
				cpf: form.cpf ? '' : 'Campo obrigatório',
			});
		};

		const validateForgot = () => {
			setValidateForgotReady(true);
			setValidateRules({
				cpf: form.cpf ? '' : 'É necessário preencher um CPF.',
			});
		};

		const handleCPF = (field: any) => (e: any) => {
			setForm({
				...form,
				[field]: e.value,
			});
		};

		const handleInput = (field: any) => (e: any) => {
			setForm({
				...form,
				[field]: e.target.value,
			});
		};

		const [refetch, { loading, data }] = useCheckDocumentLazyQuery({
			variables: { cpf: form.cpf },
			onCompleted: response => {
				if (response.checkDocument.status === responseStatus.not_found) {
					router.push({
						pathname: '/register/not-found',
						query: {
							cpf: form.cpf,
						},
					});
				} else if (response.checkDocument.status === responseStatus.active) {
					router.push({
						pathname: '/login',
						query: {
							cpf: form.cpf,
						},
					});
				} else if (response.checkDocument.status === responseStatus.onboard) {
					router.push({
						pathname: '/reset-password',
						query: {
							cpf: form.cpf,
						},
					});
				} else {
					router.push({
						pathname: `/register/${encryptString(form.cpf)}`,
					});
				}
			},
			onError: function (): void {
				router.push({
					pathname: '/register/not-found',
					query: {
						cpf: form.cpf,
					},
				});
			},
			fetchPolicy: 'network-only', // Used for first execution
			nextFetchPolicy: 'cache-first', // Used for subsequent execution
		});

		const onSubmit = e => {
			e.preventDefault();
			gtmShootEvent({
				screen: tagScreenEnum.COLLABORATOR_HOME_PAGE,
				label: 'btn-acessar',
				section: null,
				payload: null,
			});

			validate();

			setValidateRules(state => {
				if (!state.cpf) {
					refetch();
				}

				return state;
			});
		};

		const shootEvent = (id: any) => (e: any) => {
			gtmShootEvent({
				screen: tagScreenEnum.COLLABORATOR_HOME_PAGE,
				label: id,
				section: null,
				payload: null,
			});
		};

		const { logo, name, footer, theme, pages } = usePartner();

		const buttonStyle = css(`
			background-color: ${
				pages?.home?.button?.solid ? pages?.home?.button?.color : 'inherit'
			};
		`);

		return (
			<Layout>
				<Container className={styles.container}>
					<Row>
						<Col md={12} xl={12} className={styles.welcomeMobile}>
							<Image
								src={logo?.custom}
								width={logo?.width}
								height={logo?.height}
								alt={name?.standard}
							/>
						</Col>
					</Row>
					<Row>
						<Col md={12} xl={12} className={styles.welcome}>
							<div className={styles.box}>
								<Image
									className={styles.logoDesktop}
									src={logo?.custom}
									width={logo?.width}
									height={logo?.height}
									alt={name?.standard}
								/>
								<StyledDotedText
									weight="v_400"
									size="v_28"
									className={styles.label}
									color={theme?.accent}>
									Portal <strong>exclusivo</strong> para{' '}
									<strong>
										colaboradores de condomínios e administradoras.
									</strong>
								</StyledDotedText>
								<div className={styles.request}>
									<Text
										color="white"
										weight="v_400"
										size="v_30"
										className={styles.subTitle}>
										<strong>Empréstimo rápido e fácil</strong> com desconto
										direto na folha de pagamento
									</Text>

									<form className={styles.form} onSubmit={onSubmit}>
										<div>
											<CpfInput
												name="cpf"
												id="cpf"
												onChange={handleCPF('cpf')}
												onClick={shootEvent('txt-cpf')}
												error={validateRules.cpf.length > 0}
												errorMessage={validateRules.cpf}
												placeholder="Digite seu CPF"
											/>
										</div>

										<Button
											size="large"
											id="btn-acessar"
											variant={pages?.home?.button?.variant}
											className={buttonStyle}>
											{loading ? (
												<Spinner color="white" size="25px" />
											) : (
												'Acessar'
											)}
										</Button>
									</form>

									<div className={styles.info}>
										<Text
											color="white"
											weight="v_300"
											size="v_18"
											className={styles.text}>
											<strong>Problemas para acessar?</strong>
											<br />
											<a
												href={`https://api.whatsapp.com/send?phone=${footer?.whatsapp_phone}&text=Ol%C3%A1,%20estou%20com%20problemas.%20Poderia%20me%20ajudar?`}
												target="_blank"
												onClick={shootEvent('btn-clique-problema-acessar')}
												rel="noreferrer">
												Fale com nosso suporte
											</a>
										</Text>

										<a
											href={`https://api.whatsapp.com/send?phone=${footer?.whatsapp_phone}&text=Ol%C3%A1,%20estou%20com%20problemas.%20Poderia%20me%20ajudar?`}
											target="_blank"
											onClick={shootEvent('btn-problema-acessar')}
											id="btn-problema-acessar"
											rel="noreferrer">
											<Text
												color="white"
												weight="v_900"
												size="v_20"
												className={styles.whats}>
												<IconWhats /> {footer?.support_phone}
											</Text>
										</a>
									</div>
								</div>
							</div>

							<div className={styles.img}>
								<Image
									src="/images/img-consignado.png"
									width="505"
									height="655"
									alt="Consignado"
								/>
							</div>
						</Col>
					</Row>
				</Container>
			</Layout>
		);
	}),
);
